import { differenceInCalendarDays } from 'date-fns';
import React, { useMemo, useRef, useState } from 'react';
import { SVGCalendar } from '../assets/PackageSVG';
import styles from './DateRangePickerWidget.module.scss';

type DateRangePickerPayload = {
  min_date: string;
  max_date: string;
  default_from?: string;
  default_to?: string;
  submitted_value?: {
    from: string;
    to: string;
    nights: number;
  };
};

export function DateRangePickerWidget({
  payload,
  onSubmit,
}: {
  payload: DateRangePickerPayload;
  onSubmit: (value: { from: string; to: string; nights: number }) => void;
}) {
  const [from, setFrom] = useState(payload.submitted_value?.from || payload.default_from || payload.min_date);
  const [to, setTo] = useState(payload.submitted_value?.to || payload.default_to || payload.max_date);
  const fromRef = useRef<HTMLInputElement | null>(null);
  const toRef = useRef<HTMLInputElement | null>(null);
  const nights = useMemo(() => Math.max(1, differenceInCalendarDays(new Date(to), new Date(from))), [from, to]);

  const openPicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  return (
    <section className={styles.root} aria-label="Choose dates">
      <h3 className={styles.title}>Choose dates</h3>

      <div className={styles.row}>
        <label className={styles.fieldWrap}>
          <span className={styles.label}>From</span>
          <div className={styles.inputWrap}>
            <input
              ref={fromRef}
              className={styles.input}
              type="date"
              value={from}
              min={payload.min_date}
              max={payload.max_date}
              onChange={(event) => setFrom(event.target.value)}
            />
            <button
              type="button"
              className={styles.calendarButton}
              aria-label="Open from date picker"
              onClick={() => openPicker(fromRef.current)}
            >
              <SVGCalendar className={styles.calendarIcon} />
            </button>
          </div>
        </label>

        <label className={styles.fieldWrap}>
          <span className={styles.label}>To</span>
          <div className={styles.inputWrap}>
            <input
              ref={toRef}
              className={styles.input}
              type="date"
              value={to}
              min={payload.min_date}
              max={payload.max_date}
              onChange={(event) => setTo(event.target.value)}
            />
            <button
              type="button"
              className={styles.calendarButton}
              aria-label="Open to date picker"
              onClick={() => openPicker(toRef.current)}
            >
              <SVGCalendar className={styles.calendarIcon} />
            </button>
          </div>
        </label>
      </div>

      <div className={styles.footer}>
        <p className={styles.nights}>{nights} nights</p>
        <button type="button" className={styles.submitButton} onClick={() => onSubmit({ from, to, nights })}>
          Submit
        </button>
      </div>
    </section>
  );
}

